import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";
import FunctionsIcon from "@mui/icons-material/Functions";
import ConstructionIcon from "@mui/icons-material/Construction";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Autocomplete,
  Chip,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { secondAxios } from "../../../../api/axios";
import paths from "../../../../api/paths.api";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import DescriptionIcon from "@mui/icons-material/Description";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOffIcon from "@mui/icons-material/FolderOff";

import { mrtSurfaceProps } from "../../../../styles/mrtThemeProps";
import PlantModuleNav from "../../../../components/PlantModuleNav";
import { useAuthContext } from "../../../../context/authContext";
import useModuleAccess from "../../../../hooks/useModuleAccess";
import { filterColumnsByLicence } from "../../../../constants/appModules";
import ClientsCrudModal from "./ClientsCrudModal";
import SubProjectsCrudModal from "./SubProjectsCrudModal";

const buildPlantName = (prefix, number) =>
  [prefix, number].filter(Boolean).join("-");

const getPersonLabel = (person) =>
  typeof person === "object"
    ? person?.name || person?.email || "-"
    : person || "-";

// /** Fixed width shared by all icon-only navigation columns */
// const PLANT_ICON_COLUMN_SIZE = 120;

/**
 * Responsive base width for icon/navigation columns.
 *
 * The size is only the preferred width. All columns are allowed to shrink
 * below it when the browser becomes narrower and expand when more space exists.
 */
const PLANT_ICON_COLUMN_SIZE = 72;
const PLANT_ICON_COLUMN_MIN_SIZE = 20;

/**
 * Relative responsive width for every table column.
 *
 * These are proportions, not pixels.
 * CSS Grid normalizes them automatically to the available browser width.
 */
const PLANT_COLUMN_WEIGHTS = Object.freeze({
  name: 1.1,
  description: 2.2,
  client: 1,

  tasks: 0.72,
  estimation: 0.78,

  dcs: 0.72,
  mcs: 0.72,
  pcs: 0.72,
  validation: 0.72,
  cms: 0.72,
  pms: 0.72,
  mis: 0.72,

  people: 2.3,
  projects: 0.9,
  aimsStatus: 1.25,
});

const getPlantColumnId = (column) =>
  String(column?.id || column?.accessorKey || "");

const getPlantColumnWeight = (column) => {
  const columnId = getPlantColumnId(column);
  return PLANT_COLUMN_WEIGHTS[columnId] ?? 1;
};

/**
 * Applies the final responsive percentage width after all original
 * column-specific MRT props have been created.
 */
const applyResponsivePlantColumnWidth = (column, widthPx) => {
  const safeWidth = Math.max(1, widthPx);

  const currentHeadProps =
    column?.muiTableHeadCellProps &&
    typeof column.muiTableHeadCellProps === "object"
      ? column.muiTableHeadCellProps
      : {};

  const currentBodyProps =
    column?.muiTableBodyCellProps &&
    typeof column.muiTableBodyCellProps === "object"
      ? column.muiTableBodyCellProps
      : {};

  return {
    ...column,

    /*
     * MRT/TanStack now receives the REAL width of this column.
     * No fixed minimum width is allowed to survive.
     */
    size: safeWidth,
    minSize: safeWidth,
    maxSize: safeWidth,
    grow: false,

    muiTableHeadCellProps: {
      ...currentHeadProps,
      sx: {
        ...(currentHeadProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        overflow: "hidden",

        ...plantTableBodyCellFontSx,

        "& .Mui-TableBodyCell-Content-Wrapper": {
          ...(currentBodyProps.sx?.["& .Mui-TableBodyCell-Content-Wrapper"] ||
            {}),

          fontSize: `${PLANT_TABLE_BODY_FONT_SIZE} !important`,
          fontWeight: `${PLANT_TABLE_BODY_FONT_WEIGHT} !important`,
          lineHeight: "1.2 !important",
        },
      },
    },

    muiTableBodyCellProps: {
      ...currentBodyProps,
      sx: {
        ...(currentBodyProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        overflow: "hidden",
      },
    },
  };
};

/** Width weight of the generated MRT Edit column */
const PLANT_ROW_ACTIONS_WEIGHT = 0.65;

/** Shared responsive rules applied to all table cells */
const responsiveTableCellSx = {
  minWidth: "0 !important",
  flexShrink: "1 !important",
  overflow: "hidden",
};

/** Theme-aware column separators between icon columns */
const getPlantColumnSeparatorSx = (theme) => ({
  borderLeft: `2px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
});

const getPlantCellBorderSx = (theme) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
});

/**
 * One typography system for the entire table.
 *
 * Header typography is identical for ALL columns.
 * Body typography is identical for ALL columns.
 */
const PLANT_TABLE_FONT_SIZE = "clamp(0.5rem, 0.72vw, 0.82rem)";

const PLANT_TABLE_HEADER_FONT_SIZE = PLANT_TABLE_FONT_SIZE;
const PLANT_TABLE_BODY_FONT_SIZE = "clamp(0.5rem, 0.9vw, 0.95rem)";

const PLANT_TABLE_HEADER_FONT_WEIGHT = 700;
const PLANT_TABLE_BODY_FONT_WEIGHT = 400;

const plantTableHeadCellFontSx = {
  fontSize: `${PLANT_TABLE_HEADER_FONT_SIZE} !important`,
  fontWeight: `${PLANT_TABLE_HEADER_FONT_WEIGHT} !important`,
  fontStyle: "normal",
  fontFamily: "inherit",
  lineHeight: "1.05 !important",
};

const plantTableBodyCellFontSx = {
  fontSize: `${PLANT_TABLE_BODY_FONT_SIZE} !important`,
  fontWeight: `${PLANT_TABLE_BODY_FONT_WEIGHT} !important`,
  fontStyle: "normal",
  fontFamily: "inherit",
  lineHeight: "1.2 !important",
};

// const plantTableHeaderLabelSx = {
//   display: "block",
//   textAlign: "center",
//   lineHeight: 1.05,
//   ...plantTableHeadCellFontSx,
// };

const plantTableHeaderLabelSx = {
  display: "block",
  width: "100%",
  minWidth: 0,

  textAlign: "center",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",

  ...plantTableHeadCellFontSx,
};

/** MRT shallow-merges head cell props — column `sx` replaces table `sx`; keep these rules on every narrow/icon column */
// const mrtHeadCellKeepLabelsVisibleSx = {
//   ...plantTableHeadCellFontSx,
//   ...responsiveTableCellSx,
//   "& .Mui-TableHeadCell-Content": {
//     width: "100%",
//     minWidth: 0,
//     overflow: "hidden",
//     minHeight: "2.25rem",
//     alignItems: "center",
//     justifyContent: "center",
//     flexWrap: "wrap",
//   },
//   "& .Mui-TableHeadCell-Content-Labels": {
//     overflow: "visible",
//     minWidth: 0,
//     width: "100%",
//     justifyContent: "center",
//   },
//   "& .Mui-TableHeadCell-Content-Wrapper": {
//     overflow: "visible !important",
//     textOverflow: "clip",
//     whiteSpace: "normal !important",
//     minWidth: "0 !important",
//     width: "100%",
//     maxWidth: "100%",
//     textAlign: "center",
//     ...plantTableHeadCellFontSx,
//   },
//   "& .Mui-TableHeadCell-Content-Actions": {
//     display: "none",
//   },
// };

const mrtHeadCellKeepLabelsVisibleSx = {
  ...plantTableHeadCellFontSx,

  minWidth: "0 !important",
  overflow: "hidden",

  "& .Mui-TableHeadCell-Content": {
    width: "100%",
    minWidth: "0 !important",

    overflow: "hidden",

    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",

    ...plantTableHeadCellFontSx,
  },

  "& .Mui-TableHeadCell-Content-Labels": {
    width: "100%",
    minWidth: "0 !important",

    overflow: "hidden",

    alignItems: "center",
    justifyContent: "center",

    ...plantTableHeadCellFontSx,
  },

  "& .Mui-TableHeadCell-Content-Wrapper": {
    width: "100%",
    minWidth: "0 !important",
    maxWidth: "100%",

    overflow: "hidden !important",

    whiteSpace: "normal !important",
    overflowWrap: "anywhere",
    wordBreak: "break-word",

    textAlign: "center",
    textOverflow: "clip",

    ...plantTableHeadCellFontSx,
  },

  "& .Mui-TableHeadCell-Content-Wrapper *": {
    fontSize: "inherit !important",
    fontWeight: "inherit !important",
    lineHeight: "inherit !important",
  },

  "& .Mui-TableHeadCell-Content-Actions": {
    display: "none",
  },
};

const createPlantIconColumnDefaults = (separatorSx) => ({
  size: PLANT_ICON_COLUMN_SIZE,
  minSize: PLANT_ICON_COLUMN_MIN_SIZE,

  // Allows the column to shrink on narrow screens
  // and expand proportionally on wider screens.
  grow: 0.55,

  enableSorting: false,
  enableColumnActions: false,

  muiTableHeadCellProps: {
    sx: {
      ...mrtHeadCellKeepLabelsVisibleSx,
      ...responsiveTableCellSx,
      whiteSpace: "normal",
      textAlign: "center",
      verticalAlign: "middle",
      lineHeight: 1.05,
      px: {
        xs: 0,
        sm: 0.25,
        md: 0.5,
      },
      py: {
        xs: 0.25,
        md: 0.75,
      },
      backgroundColor: "background.default",
      ...separatorSx,
    },
  },

  muiTableBodyCellProps: {
    sx: {
      ...responsiveTableCellSx,
      textAlign: "center",
      verticalAlign: "middle",
      px: {
        xs: 0,
        sm: 0.2,
        md: 0.5,
      },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "background.paper",
      ...separatorSx,

      "& .Mui-TableBodyCell-Content-Wrapper": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
      },

      "& .MuiIconButton-root": {
        padding: {
          xs: "1px",
          sm: "2px",
          md: "5px",
        },
      },

      "& .MuiSvgIcon-root": {
        fontSize: "clamp(0.9rem, 1.25vw, 1.5rem)",
      },
    },
  },
});

export default function PlantsTable({ isEstimation }) {
  const theme = useTheme();
  const tableViewportRef = useRef(null);

  const [tableViewportWidth, setTableViewportWidth] = useState(() => {
    if (typeof window === "undefined") return 1200;
    return document.documentElement.clientWidth;
  });

  useLayoutEffect(() => {
    let frameId = null;

    const updateTableViewportWidth = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const element = tableViewportRef.current;

        if (!element) return;

        /*
         * IMPORTANT:
         * Do NOT use element.clientWidth here.
         *
         * The parent layout can stay wider than the browser below ~995px.
         * We instead calculate the ACTUAL visible space from the left edge
         * of this component to the right edge of the browser viewport.
         */
        const rect = element.getBoundingClientRect();

        const viewportWidth = document.documentElement.clientWidth;

        const availableWidth = Math.max(
          1,
          Math.floor(viewportWidth - Math.max(rect.left, 0) - 1),
        );

        setTableViewportWidth((previousWidth) =>
          previousWidth === availableWidth ? previousWidth : availableWidth,
        );
      });
    };

    updateTableViewportWidth();

    window.addEventListener("resize", updateTableViewportWidth);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateTableViewportWidth)
        : null;

    if (resizeObserver && tableViewportRef.current?.parentElement) {
      resizeObserver.observe(tableViewportRef.current.parentElement);
    }

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      window.removeEventListener("resize", updateTableViewportWidth);

      resizeObserver?.disconnect();
    };
  }, []);
  const plantIconColumnSeparatorSx = useMemo(
    () => getPlantColumnSeparatorSx(theme),
    [theme],
  );
  const plantCellBorderSx = useMemo(() => getPlantCellBorderSx(theme), [theme]);
  const plantIconColumnDefaults = useMemo(
    () => createPlantIconColumnDefaults(plantIconColumnSeparatorSx),
    [plantIconColumnSeparatorSx],
  );
  const navigate = useNavigate();
  const [plants, setPlants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [state, setState] = useState({
    isLoading: false,
    isError: false,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [clientsModalOpen, setClientsModalOpen] = useState(false);
  const [subProjectsPlant, setSubProjectsPlant] = useState(null);
  const [peopleModalPlant, setPeopleModalPlant] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [currentPlant, setCurrentPlant] = useState({
    name: "",
    namePrefix: "",
    nameNumber: "",
    clientId: "",
    description: "",
    sharepointLink: "",
    people: [],
  });
  const { user } = useAuthContext();
  const { hasModule, allowedModules } = useModuleAccess();
  console.log("user sadsadsdad", user.client);

  const [aimsSendingId, setAimsSendingId] = useState(null);

  const handleSendToAIMS = async (plantId) => {
    if (!plantId || aimsSendingId) return;
    try {
      setAimsSendingId(plantId);
      await secondAxios.put(`/plant/${plantId}/aims-sent`);
      setPlants((prev) =>
        prev.map((p) => (p.id === plantId ? { ...p, aimsSent: true } : p)),
      );
    } catch (err) {
      console.error("Error sending plant to AIMS:", err);
      setState((prev) => ({ ...prev, isError: true }));
    } finally {
      setAimsSendingId(null);
    }
  };

  // Load users for people selection
  const fetchUsers = async () => {
    try {
      const res = await secondAxios.get("/user");
      setAllUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await secondAxios.get("/client");
      setAllClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  // Load plants
  const fetchPlants = async () => {
    try {
      setState({ isLoading: true, isError: false });
      const res = await secondAxios.get("/plant");
      console.log("Fetched plants:", res.data);

      const adminClient = process.env.REACT_APP_CLIENT_NAME;
      const filteredPlants =
        user.client === adminClient
          ? res.data
          : Array.isArray(res.data)
            ? res.data.filter((plant) => {
                const clientName = plant.client?.name;
                const clientPrefix = plant.client?.prefix;
                return (
                  clientName === user.client || clientPrefix === user.client
                );
              })
            : [];

      setPlants(filteredPlants);
    } catch (err) {
      console.error("Error fetching plants:", err);
      setState({ isLoading: false, isError: true });
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    if (user.client) {
      console.log("fetching plants");
      fetchPlants();
    }
    fetchUsers();
    fetchClients();
  }, [user]);

  // Open modal for creating new plant
  const handleOpenCreateModal = () => {
    setModalMode("create");
    setCurrentPlant({
      name: "",
      namePrefix: "",
      nameNumber: "",
      clientId: "",
      description: "",
      sharepointLink: "",
      people: [],
    });
    setModalOpen(true);
  };

  // Open modal for editing existing plant
  const handleOpenEditModal = (plant) => {
    setModalMode("edit");
    // Extract user IDs from people array if it exists
    const peopleIds = plant.people
      ? plant.people.map((user) => (typeof user === "string" ? user : user.id))
      : [];
    setCurrentPlant({
      ...plant,
      namePrefix: "",
      nameNumber: "",
      clientId: plant.clientId || plant.client?.id || "",
      people: peopleIds,
    });
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentPlant({
      name: "",
      namePrefix: "",
      nameNumber: "",
      clientId: "",
      description: "",
      sharepointLink: "",
      people: [],
    });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setCurrentPlant((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClientChange = (client) => {
    const prefix = client?.prefix
      ? String(client.prefix)
          .replace(/[^a-zA-Z0-9]/g, "")
          .toUpperCase()
      : "";
    setCurrentPlant((prev) => {
      const next = {
        ...prev,
        clientId: client?.id || "",
        namePrefix: prefix,
      };
      return {
        ...next,
        name:
          modalMode === "create"
            ? buildPlantName(next.namePrefix, next.nameNumber)
            : prev.name,
      };
    });
  };

  const handleNameNumberChange = (value) => {
    // Allow strings like p001 or p-001
    const sanitized = value.replace(/[^a-zA-Z0-9-]/g, "");
    setCurrentPlant((prev) => {
      const next = { ...prev, nameNumber: sanitized };
      return {
        ...next,
        name: buildPlantName(next.namePrefix, next.nameNumber),
      };
    });
  };

  // Handle people selection change
  const handlePeopleChange = (selectedUsers) => {
    const userIds = selectedUsers.map((user) =>
      typeof user === "string" ? user : user.id,
    );
    setCurrentPlant((prev) => ({
      ...prev,
      people: userIds,
    }));
  };

  // Save plant (create or update)
  const handleSavePlant = async () => {
    try {
      const plantName =
        modalMode === "create"
          ? buildPlantName(currentPlant.namePrefix, currentPlant.nameNumber)
          : currentPlant.name;

      if (!plantName) {
        return;
      }

      if (
        modalMode === "create" &&
        (!currentPlant.clientId ||
          !currentPlant.namePrefix ||
          !currentPlant.nameNumber)
      ) {
        return;
      }

      // Prepare payload according to CreatePlantDto
      const payload = {
        name: plantName,
        clientId: currentPlant.clientId || null,
        description: currentPlant.description?.trim() || null,
        sharepointLink: currentPlant.sharepointLink || undefined,
        people:
          currentPlant.people && currentPlant.people.length > 0
            ? currentPlant.people
            : undefined,
      };

      if (modalMode === "create") {
        await secondAxios.post("/plant", payload);
        // Refresh plants list to get filtered results
        await fetchPlants();
      } else {
        await secondAxios.put(`/plant/${currentPlant.id}`, payload);
        // Refresh plants list to get updated data
        await fetchPlants();
      }
      handleCloseModal();
    } catch (err) {
      console.error(
        `Error ${modalMode === "create" ? "creating" : "updating"} plant:`,
        err,
      );
      setState((prev) => ({ ...prev, isError: true }));
    }
  };

  // Delete plant
  const handleDeletePlant = async (plantId) => {
    if (!window.confirm("Are you sure you want to delete this plant?")) {
      return;
    }

    try {
      await secondAxios.delete(`/plant/${plantId}`);
      // Refresh plants list to get filtered results
      await fetchPlants();
    } catch (err) {
      console.error("Error deleting plant:", err);
      setState((prev) => ({ ...prev, isError: true }));
    }
  };

  // Columns definition
  const rawColumns = useMemo(
    () =>
      filterColumnsByLicence(
        [
          // { accessorKey: "id", header: "ID", size: 50 },
          {
            accessorKey: "name",
            header: "PUID",

            Header: () => (
              <Box component="span" sx={plantTableHeaderLabelSx}>
                (Project UID)
                <br />
                PUID
              </Box>
            ),

            size: 90,
            minSize: 30,
            grow: 0.9,
            enableColumnFilter: true,
          },
          {
            accessorKey: "description",
            header: "Project No. & Description",
            size: 170,
            minSize: 45,
            grow: 1.5,
            enableColumnFilter: true,
          },
          {
            ...plantIconColumnDefaults,
            accessorKey: "sharepointLink",
            header: "Directory",
            enableColumnFilter: false,
            enableEditing: false,
            Edit: () => null,
            Header: () => (
              <Box
                component="span"
                sx={{
                  ...plantTableHeaderLabelSx,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FolderIcon fontSize="small" color="primary" />
              </Box>
            ),
            Cell: ({ cell }) => {
              const link = cell.getValue();
              if (!link) {
                return (
                  <Tooltip title="No directory link">
                    <span>
                      <IconButton size="small" disabled>
                        <FolderOffIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              }
              return (
                <Tooltip title="Open SharePoint directory">
                  <IconButton
                    size="small"
                    component="a"
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FolderIcon color="primary" />
                  </IconButton>
                </Tooltip>
              );
            },
          },
          // {
          //   accessorKey: "quickNav",
          //   header: "Quick Nav",
          //   size: 50,
          //   enableColumnFilter: false,
          //   enableColumnActions: false,
          //   Cell: ({ cell }) => (
          //     <PlantModuleNav
          //       plantId={cell.row.original.id}
          //       compact
          //       sx={{ display: "inline-flex" }}
          //     />
          //   ),
          // },
          {
            id: "client",
            accessorFn: (row) => row.client?.name || row.client?.prefix || "-",
            header: "Client",
            filterVariant: "select",
            size: 80,
            minSize: 30,
            grow: 0.75,
            enableColumnFilter: true,
          },
          {
            ...plantIconColumnDefaults,
            accessorKey: "tasks",
            header: "Action Items",
            Header: () => (
              <Box component="span" sx={plantTableHeaderLabelSx}>
                Action
                <br />
                Items
              </Box>
            ),
            enableEditing: false,
            enableColumnActions: false,
            enableColumnFilter: false,
            Edit: () => null,
            Cell: ({ cell }) => {
              return (
                <IconButton
                  size="small"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  noNavigate={true}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/plant/${cell.row.original.id}/task`);
                  }}
                >
                  <AssignmentIcon color="primary" />
                </IconButton>
              );
            },
          },
          // {
          //   accessorKey: "ga",
          //   header: "GA",
          //   Cell: ({ cell }) => {
          //     return (
          //       // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
          //       <Box
          //         display="flex"
          //         alignItems="center"
          //         gap={1}
          //         noNavigate={true}
          //         onClick={(e) => {
          //           e.stopPropagation();
          //           // console.log(
          //           //   "mcs clicked",
          //           //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
          //           //   cell.row.original
          //           // );
          //           navigate(
          //             `/projects/${
          //               cell.row.original.id
          //             }/equipments/main_tank#main-nav=${"ga"}&mcs-tab=Spec`
          //           );
          //         }}
          //       >
          //         <CategoryIcon />
          //       </Box>
          //     );
          //   },
          // },
          ...(isEstimation
            ? [
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "estimation",
                  header: "Estimation",
                  enableColumnFilter: false,
                  Cell: ({ cell }) => {
                    return (
                      // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
                      <IconButton
                        size="small"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        noNavigate={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          // console.log(
                          //   "mcs clicked",
                          //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
                          //   cell.row.original
                          // );
                          navigate(`/plant/${cell.row.original.id}/estimation`);
                        }}
                      >
                        <FunctionsIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
              ]
            : []),
          ...(!isEstimation
            ? [
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "dcs",
                  header: "EMS",
                  enableColumnFilter: false,
                  Cell: ({ cell }) => {
                    return (
                      // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
                      <IconButton
                        size="small"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        noNavigate={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          // console.log(
                          //   "mcs clicked",
                          //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
                          //   cell.row.original
                          // );
                          navigate(`/plant/${cell.row.original.id}/dcs`);
                        }}
                      >
                        <DocumentScannerIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "mcs",
                  header: "PMS",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell, row }) => {
                    // console.log("row", row.original);
                    // const users = row.original?.users || [];

                    return (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          // console.log("mcs clicked");
                          navigate(`/plant/${cell.row.original.id}/mcs`);
                          // navigate(
                          //   `/projects/${row.original.id}/equipments/main_tank#main-nav=MCS&mcs-tab=Drawing%20BOM`
                          // );
                        }}
                        // disabled={true}
                      >
                        <ManageAccountsIcon color="primary" />
                      </IconButton>
                    );
                  },
                },

                {
                  ...plantIconColumnDefaults,
                  accessorKey: "pcs",
                  header: "CMS",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell }) => {
                    return (
                      <IconButton
                        size="small"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        noNavigate={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/plant/${cell.row.original.id}/pcs`);
                        }}
                      >
                        <ConstructionIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "validation",
                  header: "Validation",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell, row }) => {
                    // console.log("row", row.original);
                    // const users = row.original?.users || [];

                    return (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/plant/${cell.row.original.id}/validation`);
                        }}
                      >
                        <ChangeHistoryIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "cms",
                  header: "COS",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell }) => {
                    return (
                      <IconButton
                        size="small"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        noNavigate={true}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/plant/${cell.row.original.id}/cms`);
                        }}
                      >
                        <ChangeCircleIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
                {
                  ...plantIconColumnDefaults,
                  accessorKey: "pms",
                  header: "PCS",
                  enableEditing: false,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Edit: () => null,
                  Cell: ({ row, cell }) => {
                    return (
                      // <a
                      //   href={`/projects/${row.original.id}/equipments/main_tank#main-nav=PMS&pms-tab=4D`}
                      // >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          console.log("pms clicked");
                          e.stopPropagation();
                          navigate(`/plant/${cell.row.original.id}/pms`);
                        }}
                      >
                        <CalendarTodayIcon color="primary" fontSize="small" />
                      </IconButton>
                      // </a>
                    );
                  },
                },

                {
                  ...plantIconColumnDefaults,
                  accessorKey: "mis",
                  header: "MIS",
                  enableEditing: false,
                  Edit: () => null,
                  enableColumnActions: false,
                  enableColumnFilter: false,
                  Cell: ({ cell, row }) => {
                    // console.log("row", row.original);
                    // const users = row.original?.users || [];

                    return (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          // console.log("mcs clicked");
                          navigate(`/plant/${cell.row.original.id}/mis`);
                          // navigate(
                          //   `/projects/${row.original.id}/equipments/main_tank#main-nav=MCS&mcs-tab=Drawing%20BOM`
                          // );
                        }}
                        // disabled={true}
                      >
                        <DescriptionIcon color="primary" />
                      </IconButton>
                    );
                  },
                },
              ]
            : []),
          {
            accessorKey: "people",
            header: "Project Manager & Team",
            size: 180,
            minSize: 45,
            grow: 1.35,
            enableColumnFilter: false,
            Cell: ({ cell, row }) => {
              const people = cell.getValue();
              if (!people || !Array.isArray(people) || people.length === 0) {
                return <span>-</span>;
              }
              const visible = people.slice(0, 2);
              const remaining = people.length - visible.length;
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexWrap: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {visible.map((person, index) => (
                    <Chip
                      key={
                        typeof person === "object"
                          ? person.id || person.email || index
                          : person
                      }
                      size="small"
                      label={getPersonLabel(person)}
                      variant="outlined"
                      sx={{
                        maxWidth: 72,
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          px: 0.75,
                        },
                      }}
                    />
                  ))}
                  {remaining > 0 && (
                    <Chip
                      size="small"
                      label={`+${remaining}`}
                      color="primary"
                      variant="outlined"
                      clickable
                      onClick={(e) => {
                        e.stopPropagation();
                        setPeopleModalPlant(row.original);
                      }}
                      sx={{ fontWeight: 600, minWidth: 36 }}
                    />
                  )}
                </Box>
              );
            },
          },
          {
            accessorKey: "projects",
            header: "Tag IDs",
            size: 72,
            minSize: 20,
            grow: 0.55,
            enableSorting: false,
            enableColumnActions: false,
            muiTableHeadCellProps: {
              sx: {
                ...mrtHeadCellKeepLabelsVisibleSx,
                ...responsiveTableCellSx,
                whiteSpace: "normal",
                textAlign: "center",
                verticalAlign: "middle",
                lineHeight: 1.2,
                py: 0.75,
                ...plantCellBorderSx,
              },
            },
            muiTableBodyCellProps: {
              sx: {
                ...responsiveTableCellSx,
                textAlign: "center",
                verticalAlign: "middle",
                px: 0.5,
                ...plantCellBorderSx,
              },
            },
            Header: () => (
              <Box component="span" sx={plantTableHeaderLabelSx}>
                TUID
                <br />
                (Tag UID)
              </Box>
            ),
            enableColumnFilter: false,
            Cell: ({ cell, row }) => {
              const plant = row.original;
              const projects = plant.projects || plant.project;

              const projectsArray = Array.isArray(projects)
                ? projects
                : projects
                  ? [projects]
                  : [];

              const clientName = process.env.REACT_APP_CLIENT_NAME;
              const count = projectsArray.filter(
                (p) => p && p.clientName === clientName,
              ).length;

              return (
                <Chip
                  size="small"
                  label={`#${count}`}
                  color="primary"
                  variant="outlined"
                  clickable
                  onClick={(e) => {
                    e.stopPropagation();
                    setSubProjectsPlant(plant);
                  }}
                  sx={{ fontWeight: 600, minWidth: 40 }}
                />
              );
            },
          },
          {
            accessorKey: "aimsStatus",
            header: isEstimation ? "EPCM" : "AIMS",
            size: 90,
            minSize: 26,
            grow: 0.7,
            enableSorting: false,
            enableColumnActions: false,
            muiTableHeadCellProps: {
              sx: {
                ...mrtHeadCellKeepLabelsVisibleSx,
                ...responsiveTableCellSx,
                textAlign: "center",
                verticalAlign: "middle",
                px: 0.5,
                ...plantCellBorderSx,
              },
            },
            muiTableBodyCellProps: {
              sx: {
                ...responsiveTableCellSx,
                textAlign: "center",
                verticalAlign: "middle",
                px: 0.5,
                ...plantCellBorderSx,
              },
            },
            enableEditing: false,
            Edit: () => null,
            enableColumnFilter: false,
            Cell: ({ row }) => {
              const rowId = row.original.id;
              const isAimsSent = Boolean(row.original.aimsSent);
              const isSending = aimsSendingId === rowId;
              const targetLabel = isEstimation ? "EPCM" : "AIMS";
              return (
                <Chip
                  size="small"
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    height: "auto",
                    justifyContent: "center",

                    "& .MuiChip-icon": {
                      flexShrink: 0,
                      fontSize: "clamp(0.75rem, 1vw, 1.1rem)",
                      ml: {
                        xs: 0,
                        md: 0.5,
                      },
                    },

                    "& .MuiChip-label": {
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                      px: {
                        xs: 0.2,
                        sm: 0.5,
                        md: 1,
                      },
                      py: 0.25,
                      fontSize: "clamp(0.58rem, 0.68vw, 0.8rem)",
                    },
                  }}
                  label={
                    isSending
                      ? "Sending..."
                      : isAimsSent
                        ? `Sent To ${targetLabel}`
                        : `Send To ${targetLabel}`
                  }
                  icon={isAimsSent ? <CheckCircleIcon /> : <AddIcon />}
                  variant={isAimsSent ? "filled" : "outlined"}
                  color={isAimsSent ? "success" : "primary"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendToAIMS(rowId);
                  }}
                  disabled={isAimsSent || isSending}
                  onDelete={undefined}
                />
              );
            },
          },
        ],
        hasModule,
        allowedModules,
        user,
      ),
    [
      aimsSendingId,
      plantIconColumnDefaults,
      plantCellBorderSx,
      plantIconColumnSeparatorSx,
      isEstimation,
      hasModule,
      allowedModules,
      user,
    ],
  );

  const { columns, rowActionsColumnWidth } = useMemo(() => {
    const visibleColumnsWeight = rawColumns.reduce(
      (total, column) => total + getPlantColumnWeight(column),
      0,
    );

    const totalWeight = visibleColumnsWeight + PLANT_ROW_ACTIONS_WEIGHT;

    /*
     * Leave a tiny safety allowance for borders/sub-pixel rounding.
     */
    const usableWidth = Math.max(1, tableViewportWidth - 2);

    const responsiveColumns = rawColumns.map((column) => {
      const weight = getPlantColumnWeight(column);

      const widthPx = (weight / totalWeight) * usableWidth;

      return applyResponsivePlantColumnWidth(column, widthPx);
    });

    const actionsWidthPx =
      (PLANT_ROW_ACTIONS_WEIGHT / totalWeight) * usableWidth;

    return {
      columns: responsiveColumns,
      rowActionsColumnWidth: Math.max(1, actionsWidthPx),
    };
  }, [rawColumns, tableViewportWidth]);

  // Top toolbar actions
  const renderTopToolbarCustomActions = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        width: "100%",
        minWidth: 0,
        gap: 1,
        p: 1,
      }}
    >
      <Button
        color="primary"
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenCreateModal}
      >
        Add Plant
      </Button>
      <Button
        color="primary"
        variant="outlined"
        onClick={() => setClientsModalOpen(true)}
      >
        Manage Clients
      </Button>
    </Box>
  );

  return (
    <>
      <Box
        ref={tableViewportRef}
        sx={{
          width: `${tableViewportWidth}px`,
          minWidth: 0,
          maxWidth: `${tableViewportWidth}px`,

          boxSizing: "border-box",

          overflowX: "hidden",
          overflowY: "visible",
        }}
      >
        <MaterialReactTable
          data={plants}
          columns={columns}
          layoutMode="grid-no-grow"
          defaultColumn={{
            size: 1,
            minSize: 1,
            maxSize: 100000,
            grow: false,
            enableSorting: false,
            enableColumnActions: false,
          }}
          displayColumnDefOptions={{
            "mrt-row-actions": {
              header: "Edit",

              size: rowActionsColumnWidth,
              minSize: rowActionsColumnWidth,
              maxSize: rowActionsColumnWidth,
              grow: false,

              enableSorting: false,
              enableColumnActions: false,

              muiTableHeadCellProps: {
                sx: {
                  ...mrtHeadCellKeepLabelsVisibleSx,

                  boxSizing: "border-box",

                  width: `${rowActionsColumnWidth}px !important`,
                  minWidth: `${rowActionsColumnWidth}px !important`,
                  maxWidth: `${rowActionsColumnWidth}px !important`,

                  flex: `0 0 ${rowActionsColumnWidth}px !important`,
                  flexBasis: `${rowActionsColumnWidth}px !important`,
                  flexGrow: "0 !important",
                  flexShrink: "0 !important",

                  textAlign: "center",
                  overflow: "hidden",

                  px: {
                    xs: 0,
                    sm: 0.15,
                    md: 0.4,
                  },

                  ...plantIconColumnSeparatorSx,
                },
              },

              muiTableBodyCellProps: {
                sx: {
                  boxSizing: "border-box",

                  width: `${rowActionsColumnWidth}px !important`,
                  minWidth: `${rowActionsColumnWidth}px !important`,
                  maxWidth: `${rowActionsColumnWidth}px !important`,

                  flex: `0 0 ${rowActionsColumnWidth}px !important`,
                  flexBasis: `${rowActionsColumnWidth}px !important`,
                  flexGrow: "0 !important",
                  flexShrink: "0 !important",

                  textAlign: "center",
                  overflow: "hidden",

                  px: {
                    xs: 0,
                    sm: 0.1,
                    md: 0.25,
                  },

                  ...plantIconColumnSeparatorSx,

                  "& .Mui-TableBodyCell-Content-Wrapper": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                    ...plantTableBodyCellFontSx,
                  },
                  ...plantTableBodyCellFontSx,
                },
              },
            },
          }}
          enableRowActions
          enableSorting={false}
          enableColumnActions={false}
          enableFacetedValues
          enableTableHead
          initialState={{ showColumnFilters: true }}
          positionActionsColumn="last"
          {...mrtSurfaceProps}
          muiTableProps={{
            sx: {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              overflow: "hidden !important",
            },
          }}
          muiTableHeadRowProps={{
            sx: {
              display: "flex !important",

              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              overflow: "hidden",

              alignItems: "stretch",
              backgroundColor: "background.default",

              "& .MuiTableCell-root": {
                boxSizing: "border-box",
                overflow: "hidden",
                verticalAlign: "middle",
                backgroundColor: "background.default",
              },
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              ...mrtHeadCellKeepLabelsVisibleSx,
              ...responsiveTableCellSx,
              backgroundColor: "background.default",
              px: {
                xs: 0.15,
                sm: 0.4,
                md: 0.75,
              },
              ...plantCellBorderSx,
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              boxSizing: "border-box",
              minWidth: "0 !important",

              fontSize: "clamp(0.42rem, 1vw, 1rem)",

              backgroundColor: "background.paper",
              overflow: "hidden",

              px: {
                xs: 0.05,
                sm: 0.15,
                md: 0.4,
                lg: 0.7,
              },

              ...plantCellBorderSx,

              /*
               * Force plain MRT values such as PUID / Client / Description
               * to inherit exactly the same responsive typography.
               */
              "& .Mui-TableBodyCell-Content-Wrapper": {
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",

                overflow: "hidden",

                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",

                ...plantTableBodyCellFontSx,
              },

              "& .Mui-TableBodyCell-Content-Wrapper > span": {
                fontSize: `${PLANT_TABLE_BODY_FONT_SIZE} !important`,
                fontWeight: `${PLANT_TABLE_BODY_FONT_WEIGHT} !important`,
                lineHeight: "1.2 !important",
              },

              /*
               * Some MRT values are rendered directly in the cell rather
               * than inside an explicit span.
               */
              "& > span": {
                fontSize: `${PLANT_TABLE_BODY_FONT_SIZE} !important`,
                fontWeight: `${PLANT_TABLE_BODY_FONT_WEIGHT} !important`,
                lineHeight: "1.2 !important",
              },
            },
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              boxSizing: "border-box",

              overflowX: "hidden !important",

              backgroundColor: "background.paper",
              border: 1,
              borderColor: "divider",
            },
          }}
          muiTopToolbarProps={{
            sx: {
              backgroundColor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
            },
          }}
          muiBottomToolbarProps={{
            sx: {
              backgroundColor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
            },
          }}
          muiTableContainerProps={{
            sx: {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              /*
               * Absolutely no horizontal scrollbar.
               */
              overflowX: "hidden !important",

              "& .MuiTable-root": {
                width: `${tableViewportWidth}px !important`,
                minWidth: `${tableViewportWidth}px !important`,
                maxWidth: `${tableViewportWidth}px !important`,
              },

              "& .MuiTableHead-root, & .MuiTableBody-root": {
                width: `${tableViewportWidth}px !important`,
                minWidth: `${tableViewportWidth}px !important`,
                maxWidth: `${tableViewportWidth}px !important`,
                overflow: "hidden",
              },

              "& .MuiTableRow-root": {
                width: `${tableViewportWidth}px !important`,
                minWidth: `${tableViewportWidth}px !important`,
                maxWidth: `${tableViewportWidth}px !important`,
                overflow: "hidden",
              },

              /*
               * MRT can render primitive accessor values directly.
               * This catches PUID, Client, Description and any other plain-text cell.
               */
              "& .MuiTableBody-root .MuiTableCell-root": {
                fontSize: `${PLANT_TABLE_BODY_FONT_SIZE} !important`,
                fontWeight: `${PLANT_TABLE_BODY_FONT_WEIGHT} !important`,
                lineHeight: "1.2 !important",
              },

              "& .MuiTableBody-root .MuiTableCell-root > *:not(.MuiIconButton-root):not(.MuiChip-root)":
                {
                  fontSize: `${PLANT_TABLE_BODY_FONT_SIZE} !important`,
                  fontWeight: `${PLANT_TABLE_BODY_FONT_WEIGHT} !important`,
                },

              "& .MuiTableCell-root": {
                boxSizing: "border-box",
                overflow: "hidden",

                px: {
                  xs: 0,
                  sm: 0.1,
                  md: 0.35,
                  lg: 0.6,
                },

                borderColor: "divider",
              },

              /*
               * Header contents are never allowed to force their column wider.
               */
              "& .Mui-TableHeadCell-Content": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",
                overflow: "hidden",
                justifyContent: "center",
              },

              "& .Mui-TableHeadCell-Content-Labels": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",
                overflow: "hidden",
                justifyContent: "center",
              },

              "& .Mui-TableHeadCell-Content-Wrapper": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                overflow: "hidden !important",

                whiteSpace: "normal !important",
                overflowWrap: "anywhere",
                wordBreak: "break-word",

                textAlign: "center",
                lineHeight: 1,
                ...plantTableHeadCellFontSx,
              },

              /*
               * Number / Description / People etc. can all shrink.
               */
              "& .Mui-TableBodyCell-Content-Wrapper": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                overflow: "hidden",

                whiteSpace: "normal !important",
                overflowWrap: "anywhere",
                wordBreak: "break-word",

                textOverflow: "clip",
                ...plantTableHeadCellFontSx,
                lineHeight: 1.1,
              },

              /*
               * Filters must NEVER impose a minimum width.
               */
              "& .MuiTextField-root, & .MuiFormControl-root": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",
                overflow: "hidden",
              },

              "& .MuiInputBase-root": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",
                overflow: "hidden",
                padding: "0 !important",
              },

              "& .MuiInputBase-input": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                padding: {
                  xs: "0 !important",
                  sm: "1px !important",
                  md: "3px !important",
                },
                ...plantTableHeadCellFontSx,
              },

              "& .MuiInputAdornment-root": {
                minWidth: "0 !important",
                maxWidth: {
                  xs: 4,
                  sm: 8,
                  md: 14,
                },
                margin: "0 !important",
                overflow: "hidden",
              },

              "& .MuiIconButton-root": {
                minWidth: "0 !important",

                padding: {
                  xs: "0px",
                  sm: "1px",
                  md: "3px",
                },
              },

              // "& .MuiSvgIcon-root": {
              //   ...plantTableHeadCellFontSx,
              // },

              "& .MuiTableBody-root .MuiSvgIcon-root": {
                fontSize: "clamp(0.65rem, 1vw, 1.15rem) !important",
              },

              "& .MuiChip-root": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",
                overflow: "hidden",
              },

              "& .MuiChip-label": {
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",

                px: {
                  xs: 0,
                  sm: 0.1,
                  md: 0.3,
                },

                //  ...plantTableHeadCellFontSx,

                fontSize: `${PLANT_TABLE_BODY_FONT_SIZE} !important`,
                fontWeight: `${PLANT_TABLE_BODY_FONT_WEIGHT} !important`,
              },

              "& .MuiChip-icon": {
                flexShrink: 0,
                margin: "0 !important",
                fontSize: "clamp(0.4rem, 0.85vw, 1rem)",
              },
            },
          }}
          renderTopToolbarCustomActions={renderTopToolbarCustomActions}
          muiTableBodyRowProps={({ row }) => ({
            onClick: (event) => {
              const target = event.target;

              if (
                !target.closest(".MuiButtonBase-root") &&
                !target.closest("a")
              ) {
                // console.log("id", paths.plant, row.original);
                // navigate(paths.plant + "/" + row.original.id);
              }
            },

            sx: {
              display: "flex !important",

              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              overflow: "hidden",

              cursor: "pointer",
              backgroundColor: "background.paper",

              "&:nth-of-type(even)": {
                backgroundColor: "action.hover",
              },

              "& .MuiTableCell-root": {
                boxSizing: "border-box",
                overflow: "hidden",
              },
            },
          })}
          renderRowActions={({ row }) => (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                minWidth: 0,
                justifyContent: "center",
                gap: {
                  xs: 0,
                  md: 0.5,
                },
              }}
            >
              <IconButton
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEditModal(row.original);
                }}
              >
                <EditIcon />
              </IconButton>
            </Box>
          )}
          muiToolbarAlertBannerProps={
            state.isError
              ? { color: "error", children: "Error loading plant data" }
              : undefined
          }
          state={{
            isLoading: state.isLoading,
            showAlertBanner: state.isError,
          }}
        />
      </Box>
      {/* Create/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalMode === "create" ? "Create New Plant" : "Edit Plant"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <Autocomplete
              options={allClients}
              getOptionLabel={(option) =>
                option?.name
                  ? `${option.name}${option.prefix ? ` (${option.prefix})` : ""}`
                  : ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={
                allClients.find((c) => c.id === currentPlant.clientId) || null
              }
              onChange={(_event, newValue) => {
                handleClientChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  required={modalMode === "create"}
                  helperText="Prefix is taken from the selected client"
                />
              )}
            />
            {modalMode === "create" ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    label="Prefix"
                    value={currentPlant.namePrefix}
                    disabled
                    required
                    sx={{ flex: "1 1 100px", minWidth: 100 }}
                    placeholder="Select client"
                    helperText={!currentPlant.clientId ? "From client" : " "}
                  />
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    -
                  </Box>
                  <TextField
                    label="Number"
                    value={currentPlant.nameNumber}
                    onChange={(e) => handleNameNumberChange(e.target.value)}
                    required
                    sx={{ flex: "1 1 140px", minWidth: 140 }}
                    placeholder="p001"
                    helperText="e.g. p001 or p-001"
                    inputProps={{ maxLength: 20 }}
                  />
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    color: "text.secondary",
                    fontSize: "0.875rem",
                  }}
                >
                  Name preview:{" "}
                  {buildPlantName(
                    currentPlant.namePrefix,
                    currentPlant.nameNumber,
                  ) || "—"}
                </Box>
              </Box>
            ) : (
              <TextField
                label="Plant Name"
                value={currentPlant.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                fullWidth
                required
              />
            )}
            <TextField
              label="Description"
              value={currentPlant.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
              multiline
              minRows={2}
              helperText="Optional plant description"
            />
            <TextField
              label="SharePoint Link"
              value={currentPlant.sharepointLink}
              onChange={(e) =>
                handleInputChange("sharepointLink", e.target.value)
              }
              fullWidth
              type="url"
              helperText="Optional SharePoint URL"
            />
            <Autocomplete
              multiple
              options={allUsers}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? allUsers.find((u) => u.id === option)?.name ||
                    allUsers.find((u) => u.id === option)?.email ||
                    option
                  : option.name || option.email || ""
              }
              isOptionEqualToValue={(option, value) => {
                const optionId =
                  typeof option === "string" ? option : option.id;
                const valueId = typeof value === "string" ? value : value.id;
                return optionId === valueId;
              }}
              value={
                currentPlant.people && allUsers.length > 0
                  ? currentPlant.people
                      .map((id) => allUsers.find((u) => u.id === id))
                      .filter((user) => user !== undefined)
                  : []
              }
              onChange={(event, newValue) => {
                handlePeopleChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="People"
                  helperText="Select users to associate with this plant"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const label =
                    typeof option === "string"
                      ? allUsers.find((u) => u.id === option)?.name ||
                        allUsers.find((u) => u.id === option)?.email ||
                        option
                      : option.name || option.email || "";
                  return (
                    <Chip
                      variant="outlined"
                      label={label}
                      {...getTagProps({ index })}
                      key={typeof option === "string" ? option : option.id}
                    />
                  );
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePlant}
            variant="contained"
            color="primary"
            disabled={
              modalMode === "create"
                ? !currentPlant.clientId ||
                  !currentPlant.namePrefix ||
                  !currentPlant.nameNumber
                : !currentPlant.name
            }
          >
            {modalMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ClientsCrudModal
        open={clientsModalOpen}
        onClose={() => setClientsModalOpen(false)}
        onClientsChange={fetchClients}
      />

      <SubProjectsCrudModal
        open={Boolean(subProjectsPlant)}
        plant={subProjectsPlant}
        onClose={() => setSubProjectsPlant(null)}
        onProjectsChange={fetchPlants}
      />

      <Dialog
        open={Boolean(peopleModalPlant)}
        onClose={() => setPeopleModalPlant(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>People — {peopleModalPlant?.name || "Plant"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Array.isArray(peopleModalPlant?.people) &&
            peopleModalPlant.people.length > 0 ? (
              peopleModalPlant.people.map((person, index) => (
                <Chip
                  key={
                    typeof person === "object"
                      ? person.id || person.email || index
                      : person
                  }
                  label={getPersonLabel(person)}
                  variant="outlined"
                  sx={{ justifyContent: "flex-start" }}
                />
              ))
            ) : (
              <Typography color="text.secondary">No people assigned</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPeopleModalPlant(null)}
            variant="outlined"
            color="secondary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
